import { useCallback } from 'react'

import useGoogleClassroom from './useGoogleClassroom'

/**
 * Hook for handling Google Classroom authentication from any component
 * Uses a dedicated auth route to handle OAuth callbacks
 */
const useGoogleClassroomAuth = () => {
  const { authorizeGoogleClassroom } = useGoogleClassroom()

  /**
   * Initiate Google Classroom authentication
   * @param {Object} options - Authentication options
   * @param {string} options.returnPath - Path to return to after auth (optional, defaults to current path)
   * @param {string} options.state - Additional state to pass through OAuth flow (optional)
   */
  const initiateAuth = useCallback(
    async (options = {}) => {
      const { returnPath, state } = options

      // Store the return path for after authentication
      const pathToStore = returnPath || window.location.pathname + window.location.search

      sessionStorage.setItem('oauth-return-path', pathToStore)

      // Build the redirect URI to our dedicated auth route
      const redirect_uri = `${window.location.origin}/auth/google-classroom`

      // Add state parameter if provided
      const redirectWithState = state ? `${redirect_uri}?state=${encodeURIComponent(state)}` : redirect_uri

      // Initiate the OAuth flow
      await authorizeGoogleClassroom(redirectWithState)
    },
    [authorizeGoogleClassroom],
  )

  /**
   * Check if we're currently in the middle of an auth flow
   */
  const isAuthInProgress = useCallback(() => {
    return sessionStorage.getItem('oauth-return-path') !== null
  }, [])

  /**
   * Clear any stored auth state (useful for cleanup)
   */
  const clearAuthState = useCallback(() => {
    sessionStorage.removeItem('oauth-return-path')
  }, [])

  return {
    initiateAuth,
    isAuthInProgress,
    clearAuthState,
  }
}

export default useGoogleClassroomAuth
